import React from "react";
import { ArrowRight, Phone } from "lucide-react";
import { useLang } from "../LangContext";
import { IMAGES, LOGO_URL, CONTACT } from "../constants";

const Stat = ({ value, label }) => (
  <div className="flex flex-col">
    <span className="font-serif-display text-3xl text-[#1e3a5f] font-semibold leading-none">{value}</span>
    <span className="text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mt-2">{label}</span>
  </div>
);

export default function Hero({ onScrollTo }) {
  const { t, lang } = useLang();

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: `url("${IMAGES.hero}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FAFAF5]/85 via-[#FAFAF5]/70 to-[#FAFAF5]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="rc-rise flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#7a8b50]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#7a8b50] font-semibold">
                {t.hero.kicker}
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="rc-rise rc-rise-d1 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#1e3a5f] font-medium leading-[1.05]"
            >
              {t.hero.title}
            </h1>

            <p className="rc-rise rc-rise-d2 mt-7 text-base md:text-lg leading-relaxed text-[#1e3a5f]/75 max-w-2xl">
              {t.hero.subtitle}
            </p>

            <div className="rc-rise rc-rise-d3 mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-cta-quote"
                onClick={() => onScrollTo("presupuesto")}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1e3a5f] text-[#FAFAF5] font-semibold text-sm tracking-wide hover:bg-[#15293f] transition-all hover:gap-3"
              >
                {t.hero.ctaQuote}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-cta-booking"
                onClick={() => onScrollTo("reserva")}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-transparent text-[#1e3a5f] border border-[#1e3a5f]/30 font-semibold text-sm tracking-wide hover:bg-[#1e3a5f] hover:text-[#FAFAF5] transition-all"
              >
                {t.hero.ctaBooking}
              </button>
            </div>

            <div className="rc-rise rc-rise-d4 mt-14 grid grid-cols-3 gap-6 max-w-md">
              <Stat value="3" label={lang === "es" ? "Servicios" : "Zerbitzu"} />
              <Stat value="L-D" label={lang === "es" ? "Atención" : "Arreta"} />
              <Stat value="100%" label={lang === "es" ? "Local" : "Bertakoa"} />
            </div>

            <a
              href={`tel:${CONTACT.phoneIntl}`}
              data-testid="hero-phone"
              className="mt-10 inline-flex items-center gap-2 text-sm text-[#1e3a5f]/70 hover:text-[#7a8b50]"
            >
              <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
            </a>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="rc-rise rc-rise-d2 relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-white shadow-xl border border-[#7a8b50]/15">
              <img
                src={LOGO_URL}
                alt="Logo Ruizcare"
                className="absolute inset-0 w-full h-full object-contain p-10"
              />
              <div className="absolute -bottom-1 left-0 right-0 h-1.5 bg-gradient-to-r from-[#1e3a5f] via-[#7a8b50] to-[#1e3a5f]" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-[#7a8b50]/15 -z-10" />
            <div className="hidden md:block absolute -top-8 -right-6 w-24 h-24 rounded-full bg-[#1e3a5f]/10 -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}
